'use client';

import React from 'react';
import { useParams, useSearchParams } from 'next/navigation';

import ContentBankFilter from './~components/contentBankFilter';

import BreadCrumb from '@/components/design/breadcrumb';
import { Container } from '@/components/design/container';
import ContentBankCard from '@/components/design/contentBankCard';
import ContentBankSearchBar from '@/components/design/contentBankSearchBar';
import { Heading } from '@/components/design/heading';
import LoadMore from '@/components/design/loadMore';
import { Skeleton } from '@/components/ui/skeleton';
import useContentBank from '@/hooks/queries/useContentBank';
import useContentBankCategories from '@/hooks/queries/useContentBankCategories';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import useTranslation from '@/hooks/useTranslation';
import routes from '@/statics/routes';
import ContentBankIcon from '@/svgs/contentBank.svg';
import recursiveFindParents from '@/utils/recursiveFindParents';

export default function ContentBank(): React.ReactElement {
  const { t } = useTranslation('pages.contentBank');
  const loadMoreRef = React.useRef(null);

  const { t: tAll } = useTranslation('');

  type RouteParams = {
    id: string;
  };
  const { id } = useParams<RouteParams>();

  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams.toString());

  const { data: categories, isFetched: isContentBankFetched } =
    useContentBankCategories();

  const {
    data: contentBank,
    hasNextPage: hasNextPageContentBank,
    fetchNextPage: fetchNextPageContentBank,
    isFetchingNextPage,
  } = useContentBank({
    queries: {
      category_id: id,
      search_tags: params.get('tags') || undefined,
    },
  });
  const posts = contentBank?.pages
    .map((page) => {
      return page.list;
    })
    .flat() || [null, null, null, null];

  useIntersectionObserver(
    loadMoreRef,
    hasNextPageContentBank,
    fetchNextPageContentBank
  );
  if (isContentBankFetched && !categories) {
    return <></>;
  }

  const currentCategory = categories?.find((item) => item?.id === id);

  return (
    <Container className="flex flex-col gap-6 mt-7.5">
      <ContentBankSearchBar categoryId={id} />
      <div className="flex justify-between items-center">
        <Heading title={t('title')} icon={<ContentBankIcon />} />
        <ContentBankFilter />
      </div>
      <div className="min-h-5">
        {categories && (
          <BreadCrumb
            list={[
              {
                title: tAll(routes.contentBankCategories.title),
                href: routes.contentBankCategories.url,
              },
              ...recursiveFindParents(currentCategory!, categories).map(
                (parent) => ({
                  title: parent.title,
                  href: routes.contentBankSubCategory.href([
                    ...recursiveFindParents(parent, categories).map(
                      ({ id }) => id
                    ),
                    parent.id,
                  ]),
                })
              ),
              { title: currentCategory!.title, href: '' },
            ]}
          />
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {posts?.map((content, index) =>
          content ? (
            <ContentBankCard key={content?.id} content={content} />
          ) : (
            <Skeleton
              key={index}
              className="aspect-[53/50] w-full rounded-xl"
            />
          )
        )}
      </div>
      <LoadMore loadMoreRef={loadMoreRef} isLoading={isFetchingNextPage} />
    </Container>
  );
}
